"use client";

import { useState } from "react";
import Link from "next/link";
import { useMutationWithAuthRedirect } from "@/lib/convexAuth";
import { api } from "../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CheckIcon, TrashIcon } from "@/components/icons";

export function ProfileSection({
  user,
}: {
  user: { username?: string; email: string; avatarUrl?: string };
}) {
  const setUsername = useMutationWithAuthRedirect(api.users.setUsername);
  const [username, setUsernameInput] = useState(user.username || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await setUsername({ username: username.trim() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error("Failed to save:", error);
      setError(error instanceof Error ? error.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>Your public profile information</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage src={user.avatarUrl} />
            <AvatarFallback className="text-lg">
              {user.username?.charAt(0) || user.email.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{user.username || "No username set"}</p>
            <p className="text-sm text-muted">{user.email}</p>
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="username" className="text-sm font-medium">
            Username
          </label>
          <Input
            id="username"
            value={username}
            onChange={(e) => setUsernameInput(e.target.value)}
            placeholder="your_username"
            autoComplete="username"
          />
          <p className="text-xs text-muted">
            3-20 chars. Letters/numbers, underscores, and hyphens only.
          </p>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium">
            Email
          </label>
          <Input id="email" value={user.email} disabled className="bg-foreground/5" />
          <p className="text-xs text-muted">Contact support to change your email</p>
        </div>

        <Button onClick={handleSave} disabled={saving} className="gap-2">
          {saved ? (
            <>
              <CheckIcon className="h-4 w-4" />
              Saved
            </>
          ) : saving ? (
            "Saving..."
          ) : (
            "Save Changes"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

export function NotificationSection({
  user,
}: {
  user: { emailNotifications: boolean; pushNotifications: boolean };
}) {
  const updateNotifications = useMutationWithAuthRedirect(api.users.updateNotifications);
  const [emailNotifications, setEmailNotifications] = useState(user.emailNotifications);
  const [pushNotifications, setPushNotifications] = useState(user.pushNotifications);
  const [saving, setSaving] = useState(false);

  const handleToggle = async (type: "email" | "push", value: boolean) => {
    setSaving(true);
    try {
      if (type === "email") {
        setEmailNotifications(value);
        await updateNotifications({ emailNotifications: value });
      } else {
        setPushNotifications(value);
        await updateNotifications({ pushNotifications: value });
      }
    } catch (error) {
      console.error("Failed to update:", error);
      if (type === "email") setEmailNotifications(!value);
      else setPushNotifications(!value);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
        <CardDescription>Choose how you want to be notified</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ToggleRow
          label="Email Notifications"
          description="Receive updates and announcements via email"
          checked={emailNotifications}
          onChange={(value) => handleToggle("email", value)}
          disabled={saving}
        />
        <ToggleRow
          label="Push Notifications"
          description="Get notified in your browser"
          checked={pushNotifications}
          onChange={(value) => handleToggle("push", value)}
          disabled={saving}
        />
      </CardContent>
    </Card>
  );
}

export function BillingSection({
  user,
}: {
  user: { subscriptionTier: string; subscriptionStatus: string };
}) {
  const billingHref = "/dashboard/settings/billing";
  const isTrialing = user.subscriptionStatus === "trialing";
  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing & Subscription</CardTitle>
        <CardDescription>Manage your subscription plan</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border border-border bg-background p-4">
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium capitalize">{user.subscriptionTier} Plan</p>
              {isTrialing && (
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  Trial
                </span>
              )}
            </div>
            <p className="text-sm text-muted capitalize">
              Status: {user.subscriptionStatus === "none" ? "No active subscription" : user.subscriptionStatus}
            </p>
          </div>
          <Link href={billingHref}>
            <Button variant="outline">
              {user.subscriptionTier === "free" ? "Upgrade" : "Manage"}
            </Button>
          </Link>
        </div>

        <p className="text-xs text-muted">All sales are final. No refunds will be issued.</p>
      </CardContent>
    </Card>
  );
}

export function DangerZoneSection() {
  const deleteAccount = useMutationWithAuthRedirect(api.users.deleteAccount);
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (confirmText !== "DELETE") return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteAccount();
      window.location.href = "/";
    } catch (error) {
      console.error("Failed to delete:", error);
      setDeleteError(error instanceof Error ? error.message : "Failed to delete account");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="border-destructive">
      <CardHeader>
        <CardTitle className="text-destructive">Danger Zone</CardTitle>
        <CardDescription>Irreversible and destructive actions</CardDescription>
      </CardHeader>
      <CardContent>
        {!confirming ? (
          <Button variant="destructive" onClick={() => setConfirming(true)}>
            <TrashIcon className="mr-2 h-4 w-4" />
            Delete Account
          </Button>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-destructive">
              This action cannot be undone. All your data will be permanently deleted.
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Type <code className="rounded bg-foreground/5 px-1">DELETE</code> to confirm
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
              />
            </div>
            {deleteError && (
              <p className="text-sm text-destructive">{deleteError}</p>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setConfirming(false);
                  setConfirmText("");
                  setDeleteError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={confirmText !== "DELETE" || deleting}
              >
                {deleting ? "Deleting..." : "Delete My Account"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-sm text-muted">{description}</p>
      </div>
      <button
        onClick={() => onChange(!checked)}
        disabled={disabled}
        className={`relative h-6 w-11 rounded-full transition-colors ${
          checked ? "bg-primary" : "bg-foreground/20"
        }`}
      >
        <span
          className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
            checked ? "translate-x-5" : ""
          }`}
        />
      </button>
    </div>
  );
}
